/* eslint-disable max-statements */
import { Button, Center, Divider, Group, Image, Loader, Stack, Text, Title, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconEye, IconTrash } from '@tabler/icons-react';
import moment from 'moment';
import { useState } from 'react';

import { CommentListItem } from '@e-origin/contracts';
import { CommentsCriteria, EORIGIN_DATE_FORMAT_WITH_TIME } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { Table, TableActionEnum, TableActions } from '@components';
import { ContentLayout } from '@features/layout';
import { defaultCommentCriteria, useComments, useDeleteComment } from '@hooks/comment';
import { EditComment } from './EditComment';

interface CommentsListProps {
  entityId: string;
  parentEntityId?: string;
}

export const CommentsList = ({ entityId, parentEntityId }: CommentsListProps) => {
  const theme = useMantineTheme();

  const [sorting, setSorting] = useState<CommentsCriteria['sorting']>(defaultCommentCriteria.sorting);
  const { data: { list: comments = [], totalItems = 0, userMap = {} } = {}, isLoading } = useComments({
    filters: { filterByEntityId: [entityId], filterByEntityParentId: [] },
    sorting,
    includeUserDetails: true,
  });
  const deleteCommentMutation = useDeleteComment();

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
  };

  const openCommentModal = (comment?: CommentListItem) => {
    modals.open({
      title: comment ? 'Edit Comment' : 'New Comment',
      children: <EditComment entityId={entityId} parentEntityId={parentEntityId} comment={comment} onHide={() => modals.closeAll()} />,
      size: 'lg',
    });
  };

  const handleTableAction = async (actionType: string, row?: CommentListItem) => {
    switch (actionType) {
      case TableActionEnum.Create: {
        openCommentModal();
        break;
      }
      case TableActionEnum.Edit: {
        openCommentModal(row);
        break;
      }
      case TableActionEnum.Delete: {
        modals.openConfirmModal({
          title: 'Confirm comment delete',
          children: (
            <Text>
              Are you sure you want to delete the comment? <br />
              This action cannot be undone!
            </Text>
          ),
          labels: { confirm: 'Delete', cancel: 'Cancel' },
          confirmProps: { color: 'red' },
          onConfirm: async () => {
            await deleteCommentMutation.mutateAsync(row._id);
            modals.closeAll();
          },
        });
        break;
      }
      default:
        break;
    }
  };

  const columns = [
    {
      name: 'Comment',
      selector: 'text',
      cell: (row) => (
        <Text lineClamp={6} style={{ wordBreak: 'break-word', minWidth: 0, width: '100%' }}>
          {row.text}
        </Text>
      ),
      sortable: true,
      width: '800px',
    },
    {
      name: 'User',
      cell: (row) => userMap[row.createdBy],
      width: '150px',
    },
    {
      name: 'Date',
      selector: 'updatedAt',
      cell: (row) => moment(row.updatedAt).format(EORIGIN_DATE_FORMAT_WITH_TIME),
      sortable: true,
      width: '150px',
    },
    {
      name: 'Actions',
      sticky: 'right' as any,
      cell: (row, rowIdx) => (
        <TableActions
          actions={[
            {
              iconComponent: <IconEye color={theme.colors[theme.primaryColor][6]} />,
              text: 'Edit',
              onClick: () => handleTableAction(TableActionEnum.Edit, row),
            },
            {
              iconComponent: <IconTrash color={theme.colors[theme.primaryColor][6]} />,
              text: 'Delete',
              onClick: () => handleTableAction(TableActionEnum.Delete, row),
            },
          ]}
          disableAfterClick={false}
          moveMenuUpwards={false}
          data-pw={rowIdx}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <>
      <Divider />
      <Stack mt={-25}>
        <ContentLayout>
          <ContentLayout.Header>
            <Title size="lg" fw="bold" ml={-25}>
              <Group>
                Comments
                <Image src={IconFactory.comment} alt="Comments" w={20} h={20} />
              </Group>
            </Title>
            <Button
              data-pw="new-comment-btn"
              onClick={() => {
                handleTableAction(TableActionEnum.Create);
              }}
              variant="outline"
              size="md"
              h={55}
            >
              New comment
            </Button>
          </ContentLayout.Header>

          <ContentLayout.Content showDivider={totalItems > 0}>
            <Table
              columns={columns}
              dataSource={comments || []}
              total={totalItems}
              onSortingChange={handleSorting}
              isLoading={isLoading}
              defaultSorting={sorting.field}
              defaultSortingDirection={sorting.direction}
            />
          </ContentLayout.Content>
        </ContentLayout>
      </Stack>
    </>
  );
};
